import { DateInMilliSec } from '@common/constans';
import { IGResponse } from '@common/models';
import { IAgenda } from '@common/models/CMS/Agenda';
import AddToCalendar from '@components/general/AddToCalendar';
import Calender from '@components/general/Calender';
import Button from '@components/general/form/Button';
import Map from "@components/general/Map";
import Navigation from '@components/general/Navigation';
import TimeSpan from '@components/general/TimeSpan';
import Title from '@components/general/Title';
import WidgetContainer from '@components/Widgets/WidgetContainer';
import { getFilePath, MapType } from '@config/appConfig';
import { Culture } from '@core/Culture';
import { renderLoading } from '@pages/BoardingPass';
import { setLoaderElement } from '@pages/CityInformation';
import { useApolloQuery } from '@service/graphql';
import { AGENDA_DETAILS } from '@service/graphql/query/Query.Agenda';
import Translate from '@service/Translate';
import { range } from '@utilize/Util';
import { FC, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import AgendaTopic from './AgendaTopic';

interface IAgendaDetailsParams {
    id: string;
}

const AgendaDetails: FC = () => {
    const { tr } = Translate;
    const { id } = useParams<IAgendaDetailsParams>();
    const [btnIndex, setBtnIndex] = useState<number>(0);
    const { loading, data } = useApolloQuery<IGResponse<IAgenda[]>>(AGENDA_DETAILS, { variables: { localizationSet: id } });
    const agendaData = data?.agenda.length ? data?.agenda[0] : undefined;
    const agendaDataFiltered = useMemo(() => agendaData?.items?.contentItems
        .filter(x => new Date(x.startTime || 0).getDate() === new Date(agendaData?.items?.contentItems[0].startTime || 0).getDate() + btnIndex), [agendaData?.items?.contentItems, btnIndex]);

    // calculate range of agenda date
    const setRangeOfAgendaDate = () => {
        const tmpDate = Culture.getDateTimeInstance(Culture.getLocale().code);
        const startDate = new Date(agendaData?.startTime!);
        const endDate = new Date(agendaData?.endTime!);
        const moreOfOneDay = startDate.getDate() !== endDate.getDate();
        agendaData?.startTime && tmpDate.setTime(startDate.getTime());
        const startDateTime = moreOfOneDay ? tmpDate.format("Y/m/d") : tmpDate.format("H:i");
        agendaData?.endTime && tmpDate.setTime(endDate.getTime());
        const endDateTime = moreOfOneDay ? tmpDate.format("Y/m/d") : tmpDate.format("H:i");
        let days = (endDate.getTime() - startDate.getTime()) / DateInMilliSec.dayPerMilliSeconds;
        days = days <= 1 ? 1 : Math.ceil(days);
        const result = {
            startDateTime, endDateTime, moreOfOneDay, days
        };

        return result
    }



    const cerateDescCalendar = (): string => {
        let currentText = '<table>';
        const end = "</table>";
        currentText = currentText.concat(`<th>${tr("agenda_date")}</th>
        <th>${tr("comment")}</th>
        <th>${tr("presenter")}</th>`)
        agendaData?.items?.contentItems.forEach(item => {
            currentText = currentText.concat(`<tr><td>${new Date(item.startTime || 0).toLocaleTimeString(Culture.getLocale().code, { hour: "2-digit", minute: "2-digit" })} - ${new Date(item.endTime || 0).toLocaleTimeString(Culture.getLocale().code, { hour: "2-digit", minute: "2-digit" })}</td>
            <td>${item.displayText ?? ""}</td>
            <td>${item.presenter ?? ""}</td></tr>`)
        });
        currentText = currentText.concat(end);
        return currentText
    }

    const setCalenderPropertiesConfig = () => {
        const endTime = new Date(agendaData?.endTime || 0).getTime();
        const currentDate = new Date().getTime()
        return endTime > currentDate;
    }

    return (
        <div className="agenda-page">
            <header className="header-page" style={{ backgroundImage: `url(${getFilePath(agendaData?.cover?.paths[0], true)})` }}>
                <article className="header-article container">
                    <h1 className="title-header">{loading ? setLoaderElement({ height: '30px', width: '50%' }) : `${agendaData?.displayText}`}</h1>
                </article>
            </header>
            <div className="container">
                <main className="agenda-detail flex">
                    <article className="article">
                        <section className="topics">
                            <header className="topics-header">
                                <Title className="group-header" text={tr("agenda_topics")} size="h2" />
                                {setRangeOfAgendaDate().days &&
                                    <div className="day-selectors">
                                        {
                                            range(setRangeOfAgendaDate().days).map(index =>
                                                <Button
                                                    key={`day_selector_btn_${index}`}
                                                    variant={btnIndex === index ? "outlined" : undefined}
                                                    onClick={() => setBtnIndex(index)}
                                                    color={btnIndex === index ? "primary" : undefined}
                                                    className={`day-btn${btnIndex === index ? " active" : ""}`}
                                                    text={tr('day_of', Culture.getLocale().numbers.find(x => x.number === index + 1)?.ordinal)}
                                                />)
                                        }
                                    </div>
                                }
                            </header>
                            <AgendaTopic
                                dataSource={agendaDataFiltered}
                                loading={loading}
                            />
                        </section>
                        <WidgetContainer
                            generalContainer={agendaData}
                        />
                    </article>
                    <aside className="aside">
                        <section className="map-container">
                            <header className="header-title">
                                <Title text={tr("agenda_location")} size="h2" />
                            </header>
                            <div className="content">
                                {
                                    loading ?
                                        renderLoading().coverElement
                                        :
                                        <Map
                                            mode={MapType.Leaflet}
                                            markers={[
                                                {
                                                    lat: agendaData?.location?.latitude || 0,
                                                    lng: agendaData?.location?.longitude || 0,
                                                    content: {
                                                        contentTitle: agendaData?.address || '',
                                                        container: agendaData?.displayText || ''
                                                    }
                                                }
                                            ]}
                                            fitBound
                                            leafletMapOption={{
                                                initial: {
                                                    dragging: false,
                                                    zoomControl: false,
                                                    scrollWheelZoom: false,
                                                    zoom: 10,
                                                    center: { lat: agendaData?.location?.latitude || 0, lng: agendaData?.location?.longitude || 0 },
                                                },
                                            }}
                                        />}
                            </div>
                            <div className="navigation-properties properties panel">{loading ?
                                <>
                                    {renderLoading().longStringBox}
                                    {renderLoading().stringBox}
                                </>
                                :
                                <>
                                    <section className="property-text">
                                        {agendaData?.address}
                                    </section>
                                    <Navigation
                                        location={{ lat: agendaData?.location?.latitude || 0, lng: agendaData?.location?.longitude || 0 }}
                                        rounded
                                    />
                                </>
                            }
                            </div>
                        </section>
                        <section className="calender-container">
                            <header className="header-title">
                                <Title text={tr("agenda_date")} size="h2" />
                                {loading ?
                                    renderLoading().longStringBox
                                    :
                                    (setCalenderPropertiesConfig() &&
                                        <div className="desc panel">
                                            {(agendaData?.startTime) &&
                                                <TimeSpan
                                                    startTime={new Date().getTime()}
                                                    endTime={new Date(agendaData?.startTime || 0).getTime()}
                                                />
                                            }
                                            <span>
                                                {tr("until", "event")}
                                            </span>
                                        </div>
                                    )
                                }
                            </header>
                            <div className="content panel">
                                <Calender
                                    renderHeader
                                    dateInstance={new Date(agendaData?.startTime || 0).getTime()}
                                    specialDayConfig={{
                                        dates: [new Date(agendaData?.startTime || 0).getTime()],
                                        style: {
                                            color: "#00a693",
                                            fontWeight: "bold",
                                        }
                                    }}
                                />
                            </div>
                            {setCalenderPropertiesConfig() &&
                                <div className="calender-properties properties panel">
                                    {loading ?
                                        <>
                                            {renderLoading().longStringBox}
                                            {renderLoading().stringBox}
                                        </>
                                        :
                                        <>
                                            <section className="property-text">
                                                {tr("of", setRangeOfAgendaDate().startDateTime)} {tr("until", setRangeOfAgendaDate().endDateTime)}
                                            </section>
                                            <AddToCalendar
                                                address={agendaData?.address}
                                                title={agendaData?.startTime && agendaData?.displayText}
                                                description={cerateDescCalendar()}
                                                startTime={new Date(agendaData?.startTime || 0)?.getTime()}
                                                endTime={new Date(agendaData?.endTime || 0)?.getTime()}
                                            />
                                        </>
                                    }
                                </div>
                            }
                        </section>
                    </aside>
                </main>
            </div >
        </div>
    )
}

export default AgendaDetails